import { SizedWord } from './wordCloudSizing'

export type { SizedWord }

export interface PlacedWord extends SizedWord {
  x: number
  y: number
  width: number
  height: number
}

interface Bounds {
  x: number
  y: number
  width: number
  height: number
}

function measureWord(
  text: string,
  fontSize: number,
  context: CanvasRenderingContext2D,
  fontFamily: string,
) {
  context.font = `600 ${fontSize}px ${fontFamily}`
  const metrics = context.measureText(text)
  return {
    width: metrics.width,
    height: fontSize * 1.2,
  }
}

function overlaps(a: Bounds, b: Bounds, padding: number) {
  return !(
    a.x + a.width + padding <= b.x ||
    b.x + b.width + padding <= a.x ||
    a.y + a.height + padding <= b.y ||
    b.y + b.height + padding <= a.y
  )
}

function fits(bounds: Bounds, width: number, height: number, padding: number) {
  return (
    bounds.x >= padding &&
    bounds.y >= padding &&
    bounds.x + bounds.width <= width - padding &&
    bounds.y + bounds.height <= height - padding
  )
}

export function layoutWordCloud(
  words: SizedWord[],
  width: number,
  height: number,
  padding: number,
  fontFamily: string,
): PlacedWord[] {
  if (!words.length || width <= 0 || height <= 0) {
    return []
  }

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) {
    return []
  }

  const centerX = width / 2
  const centerY = height / 2
  const placed: PlacedWord[] = []

  for (const word of words) {
    const { width: wordWidth, height: wordHeight } = measureWord(
      word.text,
      word.fontSize,
      context,
      fontFamily,
    )

    let position: PlacedWord | null = null

    for (let step = 0; step < 4000; step += 1) {
      const angle = step * 0.35
      const radius = step * 0.75
      const candidate: Bounds = {
        x: centerX + radius * Math.cos(angle) - wordWidth / 2,
        y: centerY + radius * Math.sin(angle) - wordHeight / 2,
        width: wordWidth,
        height: wordHeight,
      }

      if (!fits(candidate, width, height, padding)) {
        continue
      }

      const collides = placed.some((existing) => overlaps(candidate, existing, padding))
      if (!collides) {
        position = { ...word, ...candidate }
        break
      }
    }

    if (position) {
      placed.push(position)
    }
  }

  return placed
}
